<template>
  <div class="ws-modal">
    <form
      class="ws-modal__form"
      @submit.prevent="submit"
    >
      <header class="ws-modal__header">
        <div class="columns">
          <div class="column is-10">
            <h2 class="has-text-weight-bold is-size-1">
              <span v-text="$t('Управление экспертами')"></span>
            </h2>
          </div>
        </div>
      </header>
      <div
        class="ws-modal__content"
        :class="{'is-loading': isSaving}"
      >
        <ws-experts-types-buttons
          :isDisableCheckDocs="isDisableCheckDocs"
          :expert-type="userType"
          :change-type="onChangeType"
        />
        <div class="columns">
          <div class="column is-12">
            <template v-if="userType === expertTypes.expert">
              <ws-experts-search
                :expert-search="expertSearch"
                :roles="'lineal'"
                :disabled="!isMyDeCenter"
                :selected="experts"
                :exam="exam"
                multiple
                :change="updateExpertsList"
              />
              <ws-lineal-expert-card-list
                :exam="exam"
                :expertsData="experts"
                :isMyDeCenter="isMyDeCenter"
                @updateExperts='onUpdateExperts'
                @onDeleteExpert='onDeleteExpert'
              />
            </template>

            <template v-if="userType === expertTypes.user">
              <ws-users-search
                :expert-search="expertSearch"
                :selected="users"
                :disabled="isActionsDisabled"
                multiple
                :change="updateUsersList"
              />

              <ws-lineal-expert-card-list
                :exam="exam"
                :expertsData="users"
                :isMyDeCenter="isMyDeCenter"
                @updateExperts='onUpdateExperts'
                @onDeleteExpert='onDeleteUser'
              />
            </template>

            <hr v-if="isDivided" />

            <ws-lineal-expert-card-list
              :exam="exam"
              :expertsData="savedExperts"
              :isMyDeCenter="isMyDeCenter"
              @updateExperts='onUpdateExperts'
              @onDeleteExpert='onSavedDeleteExpert'
            />
          </div>
        </div>
      </div>
      <footer class="ws-modal__footer">
        <div class="columns">
          <div class="column is-8"></div>
          <div class="column is-4 is-vcentered">
            <div class="has-text-right-desktop">
              <button
                :disabled="disabledBtnSave || !isSaveAllowed || !isExpertsValid"
                type="submit"
                class="ws-button button is-large is-fullwidth"
                :class="isExpertsValid && isSaveAllowed ? 'is-primary' : 'is-danger'"
              >
                <span
                  v-if="isExpertsValid && isSaveAllowed"
                  class="is-size-4 has-text-weight-bold"
                  v-text="$t('Сохранить')"
                ></span>
                <span
                  v-else
                  class="is-size-4 has-text-weight-bold"
                  v-text="$t('Сохранение недоступно')"
                ></span>
              </button>
            </div>
          </div>
        </div>
      </footer>
    </form>
    <aside class="ws-modal__sidebar">
      <button
        class="ws-button button is-text"
        type="button"
        @click="close"
      >
        <ws-icon
          class="ws-button__icon"
          name="close-thin"
          color="#000000"
          :fill="true"
          :title="$t('Закрыть')"
          width="17"
          height="18"
        />
      </button>
    </aside>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { get } from 'lodash-es'
import expertStatus from '../demo-exams/data/expertStatusEnum'
import expertTypes from '../demo-exams/data/expertTypesEnum'
import WsUsersSearch from './componets/WsUsersSearch.vue'
import WsExpertsTypesButtons from './componets/WsExpertsTypesButtons.vue'
import WsExpertsSearch from './componets/WsExpertsSearch.vue'
import WsLinealExpertCardList from '../demo-exams/WsLinealExpertCardList.vue'
import validateExpertCertificates from '@/utils/validate-expert-certificates'
import { getPhoneNumbers } from '@/utils/getPhoneNumber'
import { constants } from '@/services/constants.service'

export default {
  components: {
    WsLinealExpertCardList,
    WsExpertsTypesButtons,
    WsUsersSearch,
    WsExpertsSearch
  },
  name: 'WsExpertsManagement',
  props: {
    exam: {
      type: Object
    },
    isMyDeCenter: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      newExam: {},
      deleted: [],
      experts: [],
      users: [],
      savedExperts: [],
      expertSearch: {
        value: null,
        guid: null
      },
      expertStatus,
      isSaving: false,
      remoteExperts: [],
      isPending: false,
      userType: expertTypes.expert,
      expertTypes
    }
  },
  watch: {
    exam: {
      deep: true,
      immediate: true,
      async handler (exam) {
        if (exam) {
          const experts = exam.experts || []
          const guids = experts.map(el => el.guid)
          let expertsList = []
          if (guids.length) {
            expertsList = await this.findExperts({
              query: {
                guids: guids,
                competency_guid: exam.accred_comps.competency.guid,
                with_light_certificates: true
              }
            })
          }
          this.savedExperts = expertsList.map(exp => this.getExpertInfo(exp, expertTypes.expert))
          this.newExam = Object.assign({}, exam)
        } else {
          this.newExam = {}
          this.savedExperts = []
        }
      }
    }
  },
  computed: {
    ...mapState('authentication', ['currentRole']),
    ...mapState('examExtraRules', ['examRules']),
    isDivided () {
      if (this.savedExperts.length) {
        const isExperts = this.userType === expertTypes.expert && this.experts.length
        const isUsers = this.userType === expertTypes.user && this.users.length
        if (isExperts || isUsers) {
          return true
        }
      }
      return false
    },
    isDisableCheckDocs () {
      const examFlags = this.exam.flags || []
      const rulesFlags = [
        ...this.examRules.attestation_type,
        ...this.examRules.level_de
      ]
      const isDisableCheck = examFlags.some(flag => rulesFlags.includes(flag))

      return isDisableCheck
    },
    isSaveAllowed () {
      if (this.isDisableCheckDocs && ['front_curator', 'front_rcc'].includes(this.currentRole)) {
        return true
      }

      const isAdmin = ['front_admin', 'front_dep'].includes(this.currentRole)
      if (isAdmin || this.userType === expertTypes.user) {
        return true
      }

      const isValidCertificates = this.experts.map(exp => {
        const isValidCertificate = validateExpertCertificates(exp.certificates, this.exam.ended_at)
        const isValidAttestation = validateExpertCertificates(exp.attestations, this.exam.ended_at)
        return isValidCertificate || isValidAttestation
      }).every(i => i)

      return isValidCertificates
    },
    disabledBtnSave () {
      let newExperts = [ ...this.experts ]
      if (this.userType === expertTypes.user) {
        newExperts = [ ...this.users ]
      }
      return (!this.deleted.length && !newExperts.length) || this.isSaving
    },
    isExpertsValid () {
      return !this.experts.find(el => el.lineal_expert_role_status !== 1)
    },
    isExamComplete () {
      return this.exam.status === constants.exam_status.COMPLETE_MAIN_EXPERT ||
        this.exam.status === constants.exam_status.COMPLETE_MAIN_EXPERT_BLOCKED
    },
    isActionsDisabled () {
      return !this.isMyDeCenter || this.isExamComplete || ['front_rcc'].includes(this.currentRole)
    }
  },
  async created () {
    this.isSaving = true
    try {
      await this.getExamRules()
    } finally {
      this.isSaving = false
    }
  },
  methods: {
    ...mapActions('exams', ['update', 'updateExpertStatus']),
    ...mapActions('refbook/experts', {
      findExperts: 'find'
    }),
    ...mapActions('examExtraRules', ['getExamRules']),
    onChangeType (type) {
      this.userType = type
      this.expertSearch = {
        guid: null,
        value: null,
        item: null
      }
    },
    onUpdateExperts (experts) {
      this.savedExperts = experts
      this.$emit('update', { ...this.newExam, experts: experts })
    },
    onDeleteExpert (guid) {
      const list = [ ...this.experts ]
      this.experts = list.filter(el => el.guid !== guid)
    },
    onDeleteUser (guid) {
      const list = [ ...this.users ]
      this.users = list.filter(el => el.guid !== guid)
    },
    onSavedDeleteExpert (guid) {
      const list = [ ...this.savedExperts ]
      this.savedExperts = list.filter(el => el.guid !== guid)
      this.deleted.push(guid)
    },
    updateUsersList (experts) {
      const list = experts.map(el => this.getExpertInfo(el, expertTypes.user))
      this.users = [ ...this.users, ...list ]
    },
    updateExpertsList (experts) {
      const list = experts.map(el => this.getExpertInfo(el, expertTypes.expert))
      this.experts = [ ...this.experts, ...list ]
    },
    async saveExperts () {
      const body = {
        update: this.experts.map(el => ({
          competency: { guid: get(this.exam, 'accred_comps.competency.guid') },
          guid: el.guid,
          region: { guid: get(this.exam, 'de_center.address.region.guid') }
        })),
        delete: [ ...this.deleted ]
      }
      return this.update([this.exam.guid, body, '/expert-to-experts'])
    },
    async saveUsersToExperts () {
      return this.update([ this.exam.guid, {
        update: this.users.map(el => el.guid),
        delete: [ ...this.deleted ]
      }, '/user-to-experts' ])
    },
    async submit () {
      this.isSaving = true
      try {
        let res = {}
        if (this.userType === expertTypes.expert) {
          res = await this.saveExperts()
        }
        if (this.userType === expertTypes.user) {
          res = await this.saveUsersToExperts()
        }

        this.save(res)
      } catch (e) {
        this.$buefy.toast.open({
          message: this.$t(`Что-то пошло не так. Попробуйте еще раз`),
          type: 'is-danger'
        })
      } finally {
        this.isSaving = false
      }
    },
    close () {
      this.$parent.close()
    },
    save (data) {
      this.$emit('close', data)
      // this.close()
    },
    getExpertInfo (user, type) {
      const expert = user.user_info || user
      const examExperts = this.exam.experts || []
      const roleStatus = type === expertTypes.expert ? user.lineal_expert_role_status : 1
      let status = expertStatus.none

      if (user.expert_exam_status) {
        status = user.expert_exam_status
      }

      const info = {
        guid: user.guid || null,
        first: get(expert, 'first', { display: '' }),
        last: get(expert, 'last', { display: '' }),
        middle: get(expert, 'middle', { display: '' }),
        region: get(user, 'region', { title: { display: '' } }),
        phoneNumbers: getPhoneNumbers(expert) || '',
        email: get(expert, 'email', ''),
        expert_exam_status: status,
        lineal_expert_role_status: roleStatus,
        expertType: type,
        certificates: user.certificates || [],
        attestations: user.attestations || [],
        local: !examExperts.find(el => el.guid === user.guid)
      }

      return info
    }
  }
}
</script>
<style>
@media screen and (min-width: 769px) {
  .ws-members__item.ws-members__item--upt {
    grid-template-columns: 128px minmax(100px, 1fr) 100px 175px;
  }
}
</style>
<style lang="scss" scoped>
.attention {
  color:#e7a035;
  font-weight: bold;
}
</style>
